import { compact } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { SmsOpsToolErrorTurn } from 'app/types/sms-agent-ops'
import { useSmsAgentOpsStore } from 'app/stores/StoreContext'
import {
  formatMinutes,
  formatSeconds,
  formatUsd,
  smsAgentOpsLocationColumns,
  smsAgentOpsTakeoverColumns,
  smsAgentOpsWorseningColumns,
  smsFailedTextColumns,
  smsOptOutColumns,
  smsStaffTakeoverColumns,
  smsTakeoverListColumns,
  smsFailedReplyColumns,
  smsReplyWaitColumns,
  smsToolErrorColumns,
  smsWorkflowColumns,
} from 'app/table-columns/sms-agent-ops'
import { useOrganization, useWorkspace } from 'app/utils'
import { StatsCardType } from 'types/StatsCardType'
import { METRIC_DEFINITIONS, aiFunnelStages } from './smsMetricDefinitions'

export type {
  FailureGroupFilter,
  SilentScope,
  SmsOpsFailedMessage,
  SmsOpsTakeover,
  WaitFilter,
  TakeoverCauseFilter,
} from 'app/types/sms-agent-ops'

const SCROLL_TO_LIST: ScrollIntoViewOptions = { behavior: `smooth`, block: `start` }

const rate = (value: number | undefined, decimals = 0) => (value === undefined ? `-` : `${value.toFixed(decimals)}%`)

/**
 * Wires the super-user SMS Ops tab to `SmsAgentOpsStore`: it pushes the page's scope
 * (`?fromDate` / `?toDate` / `?locationIds`, and the session's org) into
 * the store, asks it to load the event lists, and returns the store's data with the
 * React-only pieces (cards, table columns, the drawer, the scroll targets).
 *
 * Every value here is read from MobX observables, so `SmsAgentOpsTab` is an `observer`.
 */
export function useSmsAgentOps() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const workspace = useWorkspace()
  const organization = useOrganization()
  const store = useSmsAgentOpsStore()
  const [selectedTurn, setSelectedTurn] = useState<SmsOpsToolErrorTurn>()
  const takeoverListRef = useRef<HTMLDivElement>(null)
  const failureListRef = useRef<HTMLDivElement>(null)
  const optOutListRef = useRef<HTMLDivElement>(null)
  const toolErrorListRef = useRef<HTMLDivElement>(null)
  const replyWaitsRef = useRef<HTMLDivElement>(null)
  const failedRepliesRef = useRef<HTMLDivElement>(null)

  const fromDate = searchParams.get(`fromDate`) || moment().startOf(`month`).format(`YYYY-MM-DD`)
  const toDate = searchParams.get(`toDate`) || moment().endOf(`month`).format(`YYYY-MM-DD`)
  const locationIdsParam = searchParams.get(`locationIds`)
  const selectedLocationIds = useMemo(() => compact(locationIdsParam?.split(`,`) ?? []), [locationIdsParam])

  // Declared first so the store knows the scope before any load effect runs.
  useEffect(() => {
    store.setScope(fromDate, toDate, selectedLocationIds, organization?.id)
  }, [store, fromDate, toDate, selectedLocationIds, workspace?.id, organization?.id])

  useEffect(() => {
    store.loadLocations()
  }, [store, workspace?.id])

  useEffect(() => {
    store.loadTakeovers()
    store.loadFailures()
    store.loadOptOuts()
    store.loadAiTurns()
    store.loadLateReplies()
    store.loadWaiting()
    store.loadFailedReplies()
  }, [store, fromDate, toDate, locationIdsParam, workspace?.id, organization?.id])

  const summary = store.summary

  const card = (label: string, value: string, definition: string): StatsCardType => ({
    label,
    value,
    infoTooltip: { title: label, message: definition },
  })

  const volumeStats: StatsCardType[] = [
    card(`Texts Received`, summary.inbound.toLocaleString(`en-US`), METRIC_DEFINITIONS.inbound),
    card(`Texts Sent`, summary.outbound.toLocaleString(`en-US`), METRIC_DEFINITIONS.outbound),
    card(`AI Agent Texts`, summary.agentTexts.toLocaleString(`en-US`), METRIC_DEFINITIONS.agentTexts),
    card(`Workflow Texts`, summary.workflowTexts.toLocaleString(`en-US`), METRIC_DEFINITIONS.workflowTexts),
    card(`Staff Texts`, summary.staffTexts.toLocaleString(`en-US`), METRIC_DEFINITIONS.staffTexts),
  ]

  const agentStats: StatsCardType[] = [
    card(`AI Conversations`, summary.aiConversations.toLocaleString(`en-US`), METRIC_DEFINITIONS.aiConversations),
    card(`Reply Rate`, rate(summary.replyRate), METRIC_DEFINITIONS.replyRate),
    {
      ...card(`Takeovers`, summary.takeovers.toLocaleString(`en-US`), METRIC_DEFINITIONS.takeovers),
      onOpenClick: () => takeoverListRef.current?.scrollIntoView(SCROLL_TO_LIST),
    },
    card(`Takeover Rate`, rate(summary.takeoverRate), METRIC_DEFINITIONS.takeoverRate),
    card(`SMS Bookings`, summary.bookings.toLocaleString(`en-US`), METRIC_DEFINITIONS.bookings),
  ]

  const healthStats: StatsCardType[] = [
    {
      ...card(`Failed Texts`, summary.failedTexts.toLocaleString(`en-US`), METRIC_DEFINITIONS.failedTexts),
      onOpenClick: () => {
        store.setFailureFilter(`all`)
        failureListRef.current?.scrollIntoView(SCROLL_TO_LIST)
      },
    },
    // One decimal: failure rates live in the low single digits, where whole percents hide the signal.
    card(`Failure Rate`, rate(summary.failureRate, 1), METRIC_DEFINITIONS.failureRate),
    {
      ...card(`Opt-Outs`, summary.optOuts.toLocaleString(`en-US`), METRIC_DEFINITIONS.optOuts),
      onOpenClick: () => optOutListRef.current?.scrollIntoView(SCROLL_TO_LIST),
    },
    card(`Unanswered Rate`, rate(summary.unansweredRate), METRIC_DEFINITIONS.unansweredRate),
    card(
      `Avg Staff Response`,
      summary.avgStaffResponseMinutes === undefined ? `-` : formatMinutes(summary.avgStaffResponseMinutes),
      METRIC_DEFINITIONS.avgStaffResponse,
    ),
  ]

  const qualityStats: StatsCardType[] = [
    card(`AI Replies`, summary.aiTurns.toLocaleString(`en-US`), METRIC_DEFINITIONS.aiTurns),
    {
      ...card(`Tool Error Rate`, rate(summary.toolErrorRate, 1), METRIC_DEFINITIONS.toolErrorRate),
      onOpenClick: () => toolErrorListRef.current?.scrollIntoView(SCROLL_TO_LIST),
    },
    card(
      `Avg AI Latency`,
      summary.avgAiLatencySeconds === undefined ? `-` : formatSeconds(summary.avgAiLatencySeconds),
      METRIC_DEFINITIONS.avgAiLatency,
    ),
    card(
      `Cost / Conversation`,
      summary.costPerConversation === undefined ? `-` : formatUsd(summary.costPerConversation),
      METRIC_DEFINITIONS.costPerConversation,
    ),
  ]

  const focusLocation = ({ locationId }: { locationId?: string }) => {
    if (!locationId) return
    setSearchParams((prev) => {
      prev.set(`locationIds`, locationId)
      prev.set(`integrationStatus`, ``)
      return prev
    })
  }

  const locationColumnOptions = { showWorkspace: !workspace, onLocationClick: focusLocation }

  const locationColumns = useMemo(() => smsAgentOpsLocationColumns(locationColumnOptions), [workspace?.id])
  const takeoverColumns = useMemo(() => smsAgentOpsTakeoverColumns(locationColumnOptions), [workspace?.id])
  const worseningColumns = useMemo(
    () => smsAgentOpsWorseningColumns({ ...locationColumnOptions, metric: store.worseningMetric }),
    [workspace?.id, store.worseningMetric],
  )
  const staffColumns = useMemo(() => smsStaffTakeoverColumns(), [])
  const workflowColumns = useMemo(() => smsWorkflowColumns({ onLocationClick: focusLocation }), [])
  const takeoverListColumns = useMemo(() => smsTakeoverListColumns({ navigate }), [])
  const failedTextColumns = useMemo(() => smsFailedTextColumns({ navigate }), [])
  const optOutColumns = useMemo(() => smsOptOutColumns({ navigate }), [])
  const toolErrorColumns = useMemo(() => smsToolErrorColumns({ navigate, onTurnClick: setSelectedTurn }), [])
  const replyWaitColumns = useMemo(() => smsReplyWaitColumns({ navigate, showWorkspace: !workspace }), [workspace?.id])
  const failedReplyColumns = useMemo(
    () => smsFailedReplyColumns({ navigate, showWorkspace: !workspace }),
    [workspace?.id],
  )

  /** The banner's button: the report, filtered to the patients still waiting. */
  const showWaiting = () => {
    store.setWaitFilter(`waiting`)
    replyWaitsRef.current?.scrollIntoView(SCROLL_TO_LIST)
  }

  /** The banner's button when nobody has waited 5 minutes yet: the failed replies still owed. */
  const showFailedReplies = () => {
    store.setFailedReplyFilter(`unanswered`)
    failedRepliesRef.current?.scrollIntoView(SCROLL_TO_LIST)
  }

  return {
    fromDate,
    toDate,
    previousFromDate: store.previousFromDate,
    previousToDate: store.previousToDate,
    isLoading: store.isLoading,
    summary,
    volumeStats,
    agentStats,
    healthStats,
    qualityStats,
    funnelStages: aiFunnelStages(summary),
    daily: store.daily,
    locationRows: store.locationRows,
    locationColumns,
    leaderboardRows: store.leaderboardRows,
    takeoverColumns,
    staffRows: store.staffRows,
    staffColumns,
    worseningRows: store.worseningRows,
    worseningColumns,
    worseningMetric: store.worseningMetric,
    setWorseningMetric: store.setWorseningMetric,
    silentRows: store.silentRows,
    isSilentLoading: !store.allSilentRows,
    silentScope: store.silentScope,
    setSilentScope: store.setSilentScope,
    workflowRows: store.workflowRows,
    workflowColumns,
    listErrors: store.listErrors,
    replyWaits: store.visibleReplyWaits,
    waitCounts: store.waitCounts,
    waitFilter: store.waitFilter,
    setWaitFilter: store.setWaitFilter,
    replyWaitColumns,
    replyWaitsRef,
    waitingNow: store.replyWaits?.filter((row) => row.status === `waiting`),
    lastAiReplyDate: store.lastAiReplyDate,
    waitingCheckedAt: store.waitingCheckedAt,
    showWaiting,
    failedReplies: store.visibleFailedReplies,
    failedReplyCounts: store.failedReplyCounts,
    failedReplyFilter: store.failedReplyFilter,
    setFailedReplyFilter: store.setFailedReplyFilter,
    failedReplyColumns,
    failedRepliesRef,
    recentFailures: store.recentUnansweredFailures,
    showFailedReplies,
    visibleTakeovers: store.visibleTakeovers,
    takeoverCounts: store.takeoverCounts,
    takeoverCauseFilter: store.takeoverCauseFilter,
    setTakeoverCauseFilter: store.setTakeoverCauseFilter,
    takeoverListColumns,
    takeoverListRef,
    visibleFailures: store.visibleFailures,
    failureCounts: store.failureCounts,
    failureFilter: store.failureFilter,
    setFailureFilter: store.setFailureFilter,
    failedTextColumns,
    failureListRef,
    optOutRows: store.optOuts,
    optOutColumns,
    optOutListRef,
    toolErrorTurns: store.toolErrorTurns,
    aiTurnCount: store.aiTurns?.length,
    toolErrorColumns,
    toolErrorListRef,
    selectedTurn,
    selectedTurnLocationName: selectedTurn?.locationName ?? ``,
    closeTurn: () => setSelectedTurn(undefined),
    navigate,
  }
}

export type UseSmsAgentOpsReturn = ReturnType<typeof useSmsAgentOps>
